import React from 'react'
import PropTypes from 'prop-types'
import exact from 'prop-types-exact'
import {
  AccountType,
  BillingType,
  ContactType,
  GiftContactType,
  MembershipType,
  DigitalRecipientType,
} from 'types'
import activeIcon from 'utility-icons/completed.svg'
import warnIcon from 'utility-icons/warn.svg'
import states from 'us-state-converter'
import { APPLICATION_STEP_NAME } from 'config'
import { displayMemberName, findPrimaryBoat, formatPhoneNumber } from 'utils'

const propTypes = {
  account: AccountType,
  billing: BillingType,
  billingContact: GiftContactType,
  canEdit: PropTypes.bool.isRequired,
  contact: ContactType,
  giftMessage: PropTypes.string,
  membership: MembershipType,
  recipientContact: DigitalRecipientType,
  stepName: PropTypes.string.isRequired,
}

function StepInformation({
  account = null,
  billing = null,
  billingContact = null,
  canEdit,
  contact = null,
  giftMessage = null,
  membership = null,
  recipientContact = null,
  stepName,
}) {
  if (!canEdit) return null

  const renderContactInfo = () => {
    if (!contact || !account) return null

    return (
      <div className="step-info">
        <ContactInfo account={account} contact={contact} />
      </div>
    )
  }

  const renderBoatInfo = () => {
    if (!membership?.boats?.length) return null

    const primaryBoat = findPrimaryBoat(membership.boats)
    if (!primaryBoat) return null

    const formatAddress = (address, city, state) => {
      return [address, city, state].filter(value => !!value).join(', ')
    }

    return (
      <div className="step-info split">
        <div>
          <p>
            <strong>Primary Boat</strong>
          </p>
          <p>{`${primaryBoat.year__c} · ${primaryBoat.length__c}ft · ${primaryBoat.boat_make__c}`}</p>
        </div>
        <div>
          <p>
            <strong>Home Port</strong>
          </p>
          <p>{primaryBoat.marina_name__c}</p>
          <p>
            {formatAddress(
              primaryBoat.address__c,
              primaryBoat.home_port_city__c,
              states.abbr(primaryBoat.home_port_state__c)
            )}
          </p>
          <p>{primaryBoat.home_port_country__c}</p>
        </div>
      </div>
    )
  }

  const renderTermsInfo = () => {
    if (!billing) return null
    const isAutoRenewEnabled = billing.autoRenewalAgreement

    return (
      <div className="step-info icon-with-text">
        <img
          src={isAutoRenewEnabled ? activeIcon : warnIcon}
          alt=""
          className="status-icon"
        />
        <p>
          Automatic Renewal: {isAutoRenewEnabled ? 'Enrolled' : 'Not Enrolled'}
        </p>
      </div>
    )
  }

  const renderRecipientInfo = () => {
    if (!recipientContact) return null

    return (
      <div className="step-info split">
        <ContactInfo contact={recipientContact} showEmail={true} />
        <RecipientGiftDetails giftMessage={giftMessage} />
      </div>
    )
  }

  const renderBillingInfo = () => {
    if (!billingContact) return null

    return (
      <div className="step-info">
        <ContactInfo contact={billingContact} showEmail={true} />
      </div>
    )
  }

  switch (stepName) {
    case APPLICATION_STEP_NAME.CONTACT_INFO:
      return renderContactInfo()
    case APPLICATION_STEP_NAME.PRIMARY_BOAT_INFORMATION:
      return renderBoatInfo()
    case APPLICATION_STEP_NAME.TERMS_AND_CONDITIONS:
      return renderTermsInfo()
    case APPLICATION_STEP_NAME.RECIPIENT_INFORMATION:
      return renderRecipientInfo()
    case APPLICATION_STEP_NAME.BILLING_INFORMATION:
      return renderBillingInfo()
    default:
      return null
  }
}

StepInformation.propTypes = exact(propTypes)

export default StepInformation

function ContactInfo({ account, contact, showEmail = true }) {
  const street = account?.billingstreet || contact?.address
  const city = account?.billingcity || contact?.city
  const state = account?.billingstate || contact?.state
  const postal = account?.billingpostalcode || contact?.postalCode

  const fullName = displayMemberName(contact)

  // Prefer mobilephone — `phone` is a legacy field that edits don't update.
  const phone = contact?.mobilephone || contact?.phone

  return (
    <div>
      <p>
        <strong>{fullName}</strong>
      </p>
      {showEmail && contact.email && <p>{contact.email}</p>}
      {phone && <p>{formatPhoneNumber(phone)}</p>}
      {street && <p>{`${street}, ${city}, ${states.abbr(state)} ${postal}`}</p>}
    </div>
  )
}

const RecipientGiftDetails = ({ giftMessage }) => (
  <div className="gift-details">
    {giftMessage && (
      <div className="additional-info">
        <p>
          <strong>Gift Message</strong>
        </p>
        <p>{giftMessage}</p>
      </div>
    )}
  </div>
)
